import { Link } from 'react-router-dom';
import { ArrowRight, LifeBuoy } from 'lucide-react';
import { PageHeader } from '@/shared/layouts/PageHeader';
import { Badge } from '@/shared/components/ui/Badge';
import { Card, CardBody, CardHeader } from '@/shared/components/ui/Card';
import { Spinner } from '@/shared/components/ui/Spinner';
import { useUser } from '@/shared/hooks/useAuth';
import { ROUTES } from '@/routes/paths';
import { useServiceDeskSummary, useServiceTickets } from '@/features/service-desk/api';
import { ServiceDeskSummaryCards } from '@/features/service-desk/components/ServiceDeskSummaryCards';
import {
  formatRemainingMinutes,
  humanize,
  personName,
  priorityTone,
  statusTone,
} from '@/features/service-desk/viewModel';

/**
 * Department dashboard for Support / Customer Support users: the SLA queue is
 * their day, so this view leads with the live ticket summary and the open
 * client-support queue — the same data the Service Desk pages manage.
 */
export function SupportDashboard() {
  const user = useUser();
  const { data: summary } = useServiceDeskSummary();
  const { data: ticketList, isLoading } = useServiceTickets({ kind: 'client_support', status: '', priority: '' });
  const openTickets = (ticketList?.data ?? []).filter(
    (ticket) => ticket.status !== 'resolved' && ticket.status !== 'closed',
  );

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={`Welcome back, ${user?.displayName || 'Support Team'}`}
        description="Your client support queue and SLA health for today."
        breadcrumbs={[{ label: 'Home', to: '/' }, { label: 'Support Dashboard' }]}
      />

      <ServiceDeskSummaryCards summary={summary} />

      <Card>
        <CardHeader
          title="Open client support queue"
          subtitle={`${openTickets.length} active ticket${openTickets.length !== 1 ? 's' : ''}`}
          action={
            <Link
              to={ROUTES.serviceDesk}
              className="inline-flex items-center gap-1 text-body-sm font-medium text-primary hover:underline"
            >
              Open Service Desk
              <ArrowRight className="h-3 w-3" aria-hidden />
            </Link>
          }
        />
        <CardBody>
          {isLoading ? (
            <div className="flex h-24 items-center justify-center">
              <Spinner />
            </div>
          ) : openTickets.length === 0 ? (
            <p className="flex items-center justify-center gap-2 py-6 text-body-sm text-on-surface-variant">
              <LifeBuoy className="h-4 w-4" aria-hidden />
              The queue is clear — no open client tickets.
            </p>
          ) : (
            <ul className="flex flex-col gap-2">
              {openTickets.slice(0, 8).map((ticket) => (
                <li
                  key={ticket.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border p-3"
                >
                  <div className="min-w-0">
                    <p className="truncate font-medium text-on-surface">
                      <span className="mr-2 font-mono text-body-sm text-on-surface-variant">{ticket.ticketNumber}</span>
                      {ticket.title}
                    </p>
                    <p className="text-body-sm text-on-surface-variant">
                      {personName(ticket.assignedTo)} · {formatRemainingMinutes(ticket.sla.remainingMinutes)}
                    </p>
                  </div>
                  <span className="flex shrink-0 gap-2">
                    <Badge tone={priorityTone(ticket.priority)}>{humanize(ticket.priority)}</Badge>
                    <Badge tone={statusTone(ticket.status)}>{humanize(ticket.status)}</Badge>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
